import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import {
  SYNC_PROVIDER_LABELS,
  SYNC_TYPE_LABELS,
  SYNC_MODE_LABELS,
  SYNC_RUN_STATUS_LABELS,
  SYNC_RUN_STATUS_COLORS,
  SYNC_RUN_STATUSES,
  RESULT_STATUSES,
} from '../../../constants/ingestionSyncRuns';
import {
  formatDuration, describeMappingSet, formatSectionLabel, describeTrigger,
} from '../../../utils/ingestionSyncRuns';
import {
  SummaryGrid, SummaryTile, SummaryLabel, SummaryValue, SummaryMeta,
} from './styles';
import { getChipSx } from '../DirectManagerDiscrepancies/chipStyles';

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ERROR_PREVIEW_LIMIT = 80;
const truncate = (s) => (s && s.length > ERROR_PREVIEW_LIMIT ? `${s.slice(0, ERROR_PREVIEW_LIMIT)}…` : s);

const CounterTile = ({ label, value, meta, danger }) => (
  <SummaryTile>
    <SummaryLabel>{label}</SummaryLabel>
    <SummaryValue style={danger && value > 0 ? { color: '#991B1B' } : undefined}>{value ?? 0}</SummaryValue>
    {meta && <SummaryMeta>{meta}</SummaryMeta>}
  </SummaryTile>
);

const SummaryCard = ({ run, batch }) => {
  if (!run) {
    return null;
  }
  const isFailed = run.status === SYNC_RUN_STATUSES.FAILED;
  const extraCounters = run.extraCounters && typeof run.extraCounters === 'object'
    ? Object.entries(run.extraCounters).filter(([, value]) => typeof value === 'number')
    : [];

  return (
    <SummaryGrid>
      <SummaryTile>
        <SummaryLabel>Sync</SummaryLabel>
        <SummaryValue style={{ fontSize: 18 }}>
          {`${SYNC_PROVIDER_LABELS[run.provider] || run.provider} · ${SYNC_TYPE_LABELS[run.syncType] || run.syncType}`}
        </SummaryValue>
        <SummaryMeta>
          {`${SYNC_MODE_LABELS[run.mode] || run.mode} · ${describeTrigger({ trigger: run.trigger, triggeredByUser: run.triggeredByUser || batch?.triggeredByUser, origin: run.origin })}`}
          {run.scope === 'SCOPED' ? ' · scoped (part of the population)' : ''}
        </SummaryMeta>
      </SummaryTile>
      <SummaryTile>
        <SummaryLabel>Status</SummaryLabel>
        <SummaryValue>
          <Chip
            size="small"
            label={SYNC_RUN_STATUS_LABELS[run.status] || run.status}
            sx={getChipSx(SYNC_RUN_STATUS_COLORS[run.status])}
          />
        </SummaryValue>
        <SummaryMeta>
          Started: {fmtDate(run.startedAt)}<br />
          Finished: {fmtDate(run.finishedAt)}<br />
          Duration: {formatDuration(run.durationMs)}
          {isFailed && (
            <>
              <br />
              <Tooltip title={run.errorMessage || 'No error message recorded'} arrow>
                <span style={{ color: '#d32f2f' }}>Error: {truncate(run.errorMessage) || '(no message)'}</span>
              </Tooltip>
            </>
          )}
        </SummaryMeta>
      </SummaryTile>
      <CounterTile label="Created" value={run.createdCount} />
      <CounterTile label="Updated" value={run.updatedCount} />
      <CounterTile label="Unchanged" value={run.unchangedCount} meta="stored, not listed here" />
      <CounterTile label="Skipped" value={run.skippedCount} />
      <CounterTile label="Errors" value={run.errorCount} danger />
      {extraCounters.map(([key, value]) => (
        <CounterTile key={key} label={formatSectionLabel(key)} value={value} />
      ))}
      <SummaryTile>
        <SummaryLabel>Job position mappings</SummaryLabel>
        <SummaryValue style={{ fontSize: 18 }}>{describeMappingSet(run)}</SummaryValue>
        <SummaryMeta>mapping set used by this run</SummaryMeta>
      </SummaryTile>
      {run.resultStatus !== RESULT_STATUSES.COMPLETE && (
        <SummaryTile>
          <SummaryLabel>Result</SummaryLabel>
          <SummaryValue style={{ fontSize: 18, color: '#B45309' }}>
            {run.resultStatus === RESULT_STATUSES.PENDING ? 'Still uploading' : 'Partial'}
          </SummaryValue>
          <SummaryMeta>
            {run.resultStatus === RESULT_STATUSES.PENDING
              ? 'item lists are still arriving — refresh in a minute'
              : `${(run.missingParts || []).length} result part(s) never arrived; counters above are complete`}
          </SummaryMeta>
        </SummaryTile>
      )}
    </SummaryGrid>
  );
};

export default SummaryCard;
